import _ from "lodash";
import {arrayIsNotEmpty} from "@/helper/Array";
import {isDefined} from "@/helper/Value";

export function prepareCareerQuestionnaire (career, skipped)
{
	if (!career)
	{
		if (career === undefined)
		{
			career = {
				items: [],
				skipped
			};
		}

		return career;
	}

	const data = _.cloneDeep(career);

	data.items = data.items
		? prepareCareer(data.items)
		: [];

	if (!isDefined(data.skipped))
	{
		data.skipped = skipped;
	}

	return data;
}

export function prepareCareer (career)
{
	if (!career)
	{
		return career;
	}

	const data = _.cloneDeep(career);

	_.forEach(data, item =>
	{
		if (item.careerType && _.isObject(item.careerType))
		{
			item.careerType = item.careerType.id;
		}

		if (item.functions)
		{
			item.functions = prepareCharacteristic(item.functions);
		}

		if (item.careerLevel && _.isObject(item.careerLevel))
		{
			item.careerLevel = item.careerLevel.id;
		}

		if (item.industrialSectors)
		{
			item.industrialSectors = prepareCharacteristic(item.industrialSectors);
		}

		if (item.organisationalArea)
		{
			item.organisationalArea = prepareCharacteristic(item.organisationalArea);
		}

		if (item.professions)
		{
			item.professions = prepareCharacteristic(item.professions);
		}

		if (item.appraiserAuthority)
		{
			item.appraiserAuthority = prepareCharacteristic(item.appraiserAuthority);
		}

		if (item.schoolLocation)
		{
			item.schoolLocation = prepareCharacteristic(item.schoolLocation);
		}

		item.schoolType = item.schoolType && _.isObject(item.schoolType)
			? item.schoolType.id
			: undefined;

		item.educationCompletion = item.educationCompletion && _.isObject(item.educationCompletion)
			? item.educationCompletion.id
			: undefined;

		item.educationCompletionType = item.educationCompletionType && _.isObject(item.educationCompletionType)
			? item.educationCompletionType.id
			: undefined;

		item.furtherInstitutionType = item.furtherInstitutionType && _.isObject(item.furtherInstitutionType)
			? item.furtherInstitutionType.id
			: undefined;

		item.workPermission = item.workPermission && _.isObject(item.workPermission)
			? item.workPermission.id
			: undefined;

		item.expertProfession = item.expertProfession && _.isObject(item.expertProfession)
			? item.expertProfession.id
			: undefined;

		if (item.scopeCountries)
		{
			item.scopeCountries = _.map(
				item.scopeCountries,
				country => _.isObject(country) ? country.id : country
			);
		}
	});

	return data;
}

export function prepareExperience (experience)
{
	if (!experience)
	{
		return experience;
	}

	// only those with relevant data
	const data = _.filter(
		_.cloneDeep(experience),
		item => (item.experience !== undefined
			|| item.selfAssessment !== undefined
			|| arrayIsNotEmpty(item.educationsFinished)
			|| arrayIsNotEmpty(item.educationsUnfinished)
		)
	);

	return prepareCharacteristic(data);
}

export function prepareExperienceSkippable (data, skipped)
{
	return prepareItemsSkippable(data, skipped, items => prepareExperience(items));
}

export function prepareCharacteristicSkippable (data, skipped)
{
	return prepareItemsSkippable(data, skipped, items => prepareCharacteristic(items));
}

export function prepareRelocation (relocation)
{
	let data = _.cloneDeep(relocation);

	if (!data)
	{
		return data;
	}

	if (data.geo)
	{
		data.geo = prepareCharacteristic(data.geo);
	}

	if (data.placeOfWork)
	{
		data.placeOfWork = prepareCharacteristic(data.placeOfWork);
	}

	if (data.premise)
	{
		data.premise = prepareCharacteristic(data.premise);
	}

	return data;
}

export function prepareTravelWillingness (travelWillingness)
{
	let data = _.cloneDeep(travelWillingness);

	if (!data)
	{
		return data;
	}

	if (data.scope)
	{
		data.scope = data.scope?.id;
	}

	if (data.externalSleepoverScope)
	{
		data.externalSleepoverScope = data.externalSleepoverScope.id;
	}

	return data;
}

export function prepareManagement (management)
{
	let data = _.cloneDeep(management);

	if (!data)
	{
		return data;
	}

	if (data.desired)
	{
		data.desired = prepareCharacteristic(data.desired);
	}

	return data;
}

export function prepareCustomerContact (customerContact)
{
	let data = _.cloneDeep(customerContact);

	if (!data)
	{
		return data;
	}

	if (data.desired)
	{
		data.desired = prepareCharacteristic(data.desired);
	}

	return data;
}

export function prepareWorkingTime (workingTime)
{
	let data = _.cloneDeep(workingTime);

	if (!data)
	{
		return data;
	}

	if (data.workingTime)
	{
		data.workingTime = prepareCharacteristic(data.workingTime);
	}

	return data;
}

export function preparePublicTransport (publicTransport)
{
	let data = _.cloneDeep(publicTransport);

	if (!data)
	{
		return data;
	}

	if (data.publicTransport)
	{
		data.publicTransport = prepareCharacteristic(data.publicTransport);
	}

	return data;
}

export function prepareOvertime (overtime)
{
	let data = _.cloneDeep(overtime);

	if (!data)
	{
		return data;
	}

	if (data.overtime)
	{
		data.overtime = prepareCharacteristic(data.overtime);
	}

	return data;
}

export function prepareSearchStatus (searchStatus)
{
	let data = _.cloneDeep(searchStatus);

	if (!data)
	{
		return data;
	}

	if (data.searchStatus)
	{
		data.searchStatus = data.searchStatus[0].item.id;
	}

	return data;
}

export function prepareParking (parking)
{
	let data = _.cloneDeep(parking);

	if (!data)
	{
		return data;
	}

	if (data.items)
	{
		data.items = prepareCharacteristic(data.items);
	}

	return data;
}

export function prepareDesireExperience (data)
{
	if (!data)
	{
		return data;
	}

	data = _.cloneDeep(data);

	data.desired = prepareCharacteristic(data.desired);
	data.experience = prepareCharacteristic(data.experience);
	data.skills = prepareCharacteristic(data.skills);

	return data;
}

export function prepareCharacteristicWithExperience (items)
{
	const preparedItems = prepareCharacteristic(items);

	return {
		desired: preparedItems,
		experience: _.filter(preparedItems, item => isDefined(item.experience) || isDefined(item.selfAssessment)),
	};
}

export function prepareCharacteristic (items)
{
	let preparedItems = [];

	_.forEach(_.cloneDeep(items), item =>
	{
		preparedItems.push(
			prepareSingleCharacteristic(item)
		);
	});

	return preparedItems;
}

export function prepareSingleCharacteristic (item)
{
	if (_.isObject(item.item))
	{
		item.item = item.item.id;
	}

	_.forEach(item.specificities || [], specificityItem =>
	{
		if (_.isObject(specificityItem.specificity))
		{
			specificityItem.specificity = specificityItem.specificity.id;
		}

		if (specificityItem.specificityType && _.isObject(specificityItem.specificityType))
		{
			specificityItem.specificityType = specificityItem.specificityType.id;
		}
	});

	_.forEach(item.supplements || [], supplement =>
	{
		if (_.isObject(supplement.characteristicType))
		{
			supplement.characteristicType = supplement.characteristicType.id;
		}

		supplement.items = _.map(
			supplement.items,
			item =>
			{
				return prepareSingleCharacteristic(item);
			}
		);
	});

	if (item.experience && _.isObject(item.experience))
	{
		item.experience = item.experience.id;
	}

	if (item.selfAssessment && _.isObject(item.selfAssessment))
	{
		item.selfAssessment = item.selfAssessment.id;
	}

	return item;
}

function prepareItemsSkippable (data, skipped, itemsPrepareFunction)
{
	if (!data)
	{
		if (data === undefined)
		{
			data = {
				items: [],
				skipped
			};
		}

		return data;
	}

	data.items = data.items
		? itemsPrepareFunction(data.items)
		: [];

	if (!isDefined(data.skipped))
	{
		data.skipped = skipped;
	}

	return data;
}
